import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { 
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  ChevronDown,
  Clock,
  CreditCard,
  DollarSign, 
  Filter,
  MoreHorizontal,
  Plus, 
  Receipt,
  Search,
  Users} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

type PaymentStatus = "pending" | "completed" | "overdue" | "cancelled";
type PaymentType = "rent" | "deposit" | "utility" | "maintenance" | "other";

interface Payment {
  id: number;
  amount: number;
  status: PaymentStatus;
  type: PaymentType;
  dueDate: string;
  createdAt: string;
  property: {
    id: number;
    title: string;
    address: string;
    image: string;
  };
  tenant?: {
    id: number;
    name: string;
    email: string;
    avatar: string;
  };
  description?: string;
}

const paymentTypes = [
  { value: "rent", label: "Орендна плата" },
  { value: "deposit", label: "Депозит" },
  { value: "utility", label: "Комунальні послуги" },
  { value: "maintenance", label: "Обслуговування" },
  { value: "other", label: "Інше" }
];

const mockPayments: Payment[] = [
  {
    id: 1,
    amount: 12000,
    status: "completed",
    type: "rent",
    dueDate: "2024-02-15",
    createdAt: "2024-02-01",
    property: {
      id: 1,
      title: "Сучасна квартира в центрі",
      address: "вул. Шевченка, 10, Київ",
      image: "https://picsum.photos/seed/apartment1/800/600"
    },
    tenant: {
      id: 1,
      name: "Іван Петренко",
      email: "ivan@example.com",
      avatar: "https://picsum.photos/seed/tenant1/200/200"
    }
  },
  {
    id: 2,
    amount: 25000,
    status: "pending",
    type: "deposit",
    dueDate: "2024-02-20",
    createdAt: "2024-02-05",
    property: {
      id: 2,
      title: "Офісне приміщення",
      address: "вул. Хрещатик, 15, Київ",
      image: "https://picsum.photos/seed/office1/800/600"
    },
    tenant: {
      id: 2,
      name: "ТОВ 'Інновації'",
      email: "office@innovations.com",
      avatar: "https://picsum.photos/seed/tenant2/200/200"
    }
  },
  {
    id: 3,
    amount: 3500,
    status: "overdue",
    type: "utility",
    dueDate: "2024-02-10",
    createdAt: "2024-02-01",
    property: {
      id: 3,
      title: "Котедж з басейном",
      address: "вул. Паркова, 5, Київ",
      image: "https://picsum.photos/seed/house1/800/600"
    },
    tenant: {
      id: 3,
      name: "Марія Коваленко",
      email: "maria@example.com",
      avatar: "https://picsum.photos/seed/tenant3/200/200"
    }
  }
];

const PaymentsPage = () => {
  const [selectedType, setSelectedType] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Payment | "property.title" | "tenant.name";
    direction: "asc" | "desc";
  }>({ key: "dueDate", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalIncome = mockPayments
    .filter(payment => payment.status === "completed")
    .reduce((sum, payment) => sum + payment.amount, 0);

  const pendingAmount = mockPayments
    .filter(payment => payment.status === "pending")
    .reduce((sum, payment) => sum + payment.amount, 0);

  const overdueAmount = mockPayments
    .filter(payment => payment.status === "overdue")
    .reduce((sum, payment) => sum + payment.amount, 0);

  type NestedKey<T> = {
    [K in keyof T]: T[K] extends object
      ? `${string & K}.${string & keyof T[K]}`
      : K;
  }[keyof T];

  const getNestedValue = (obj: any, path: string) => {
    return path.split(".").reduce((acc, part) => acc?.[part], obj);
  };

  const filteredPayments = mockPayments
    .filter(payment => 
      (selectedType === "all" || !selectedType ? true : payment.type === selectedType) &&
      (searchQuery ? 
        payment.property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.property.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.tenant?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.tenant?.email.toLowerCase().includes(searchQuery.toLowerCase())
        : true
      )
    )
    .sort((a, b) => {
      const aValue = sortConfig.key.includes(".") 
        ? getNestedValue(a, sortConfig.key)
        : a[sortConfig.key as keyof Payment];
      const bValue = sortConfig.key.includes(".")
        ? getNestedValue(b, sortConfig.key)
        : b[sortConfig.key as keyof Payment];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc" 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      return 0;
    });

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case "completed":
        return "bg-green-500/10 text-green-500";
      case "pending":
        return "bg-yellow-500/10 text-yellow-500";
      case "overdue":
        return "bg-red-500/10 text-red-500";
      case "cancelled":
        return "bg-gray-500/10 text-gray-500";
    }
  };

  const getStatusText = (status: PaymentStatus) => {
    switch (status) {
      case "completed":
        return "Оплачено";
      case "pending":
        return "Очікує оплати";
      case "overdue":
        return "Прострочено";
      case "cancelled":
        return "Скасовано";
    }
  };

  const getPaymentTypeText = (type: PaymentType) => {
    return paymentTypes.find(t => t.value === type)?.label || type;
  };

  return (
    <div className="container mx-auto p-8 space-y-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <DollarSign className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Управління платежами</h1>
            <p className="text-muted-foreground">Контроль та моніторинг платежів</p>
          </div>
        </div>
        <Button asChild>
          <Link to="/home/payments/new">
            <Plus className="mr-2 h-4 w-4" />
            Новий платіж
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Загальний дохід
            </CardTitle>
            <ArrowUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalIncome.toLocaleString()} ₴</div>
            <p className="text-xs text-muted-foreground">
              за весь період
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Очікує оплати
            </CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingAmount.toLocaleString()} ₴</div>
            <Progress 
              value={pendingAmount / (totalIncome + pendingAmount) * 100} 
              className="h-2 mt-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Прострочені платежі
            </CardTitle>
            <ArrowDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overdueAmount.toLocaleString()} ₴</div>
            <p className="text-xs text-muted-foreground">
              потребують уваги
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Платежів цього місяця
            </CardTitle>
            <Receipt className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockPayments.filter(p => new Date(p.dueDate).getMonth() === new Date().getMonth()).length}
            </div>
            <p className="text-xs text-muted-foreground">
              заплановано
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Пошук за назвою, адресою або орендарем..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Select
            value={selectedType}
            onValueChange={setSelectedType}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Тип платежу" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Всі типи</SelectItem>
              {paymentTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="mr-2 h-4 w-4" />
                Сортування
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Сортувати за</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setSortConfig({ key: "dueDate", direction: "asc" })}
              >
                Дата (за зростанням)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSortConfig({ key: "dueDate", direction: "desc" })}
              >
                Дата (за спаданням)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSortConfig({ key: "amount", direction: "desc" })}
              >
                Сума (за спаданням)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSortConfig({ key: "property.title", direction: "asc" })}
              >
                Об'єкт (А-Я)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Об'єкт / Орендар</TableHead>
              <TableHead>Тип</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right">Сума</TableHead>
              <TableHead>Термін оплати</TableHead>
              <TableHead className="text-right">Дії</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedPayments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-md overflow-hidden">
                      <img
                        src={payment.property.image}
                        alt={payment.property.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium">{payment.property.title}</div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {payment.tenant && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <div className="flex items-center gap-1">
                                  <div className="h-4 w-4 rounded-full overflow-hidden">
                                    <img
                                      src={payment.tenant.avatar}
                                      alt={payment.tenant.name}
                                      className="h-full w-full object-cover"
                                    />
                                  </div>
                                  <span>{payment.tenant.name}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{payment.tenant.email}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="rounded-md">
                    {getPaymentTypeText(payment.type)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant="secondary" 
                    className={cn("rounded-md", getStatusColor(payment.status))}
                  >
                    {getStatusText(payment.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-medium">
                  {payment.amount.toLocaleString()} ₴
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    {new Date(payment.dueDate).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Позначити як оплачений
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Calendar className="mr-2 h-4 w-4" />
                        Змінити термін
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Receipt className="mr-2 h-4 w-4" />
                        Квитанція
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600">
                        Скасувати
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage(page => Math.max(1, page - 1));
                  }}
                  className={cn(currentPage === 1 && "pointer-events-none opacity-50")}
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(page);
                    }}
                    isActive={currentPage === page}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage(page => Math.min(totalPages, page + 1));
                  }}
                  className={cn(currentPage === totalPages && "pointer-events-none opacity-50")}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage; 